import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Activity, Bell, CheckCircle2, LogOut, RefreshCw, ShieldCheck, Volume2, VolumeX } from "lucide-react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";

function CallState({ state }) {
  if (state === "ringing") return <span className="sbc-tag tag-warn">TOCANDO</span>;
  if (state === "dialing") return <span className="sbc-tag tag-warn">CHAMANDO</span>;
  if (state === "active") return <span className="sbc-tag tag-online">EM CHAMADA</span>;
  if (state === "hold") return <span className="sbc-tag tag-warn">EM ESPERA</span>;
  return <span className="text-[10px] font-mono text-[color:var(--text-muted)]">LIVRE</span>;
}

function CallQuality({ quality }) {
  const state = quality?.state || "idle";
  const details = quality?.packets != null
    ? `Perda: ${quality.loss_pct}% · Jitter: ${quality.jitter_pct}% · Pacotes: ${quality.packets}`
    : "A qualidade RTP fica disponível após o atendimento.";
  if (state === "excellent") return <span className="sbc-tag tag-online" title={details}>EXCELENTE</span>;
  if (state === "good") return <span className="sbc-tag tag-online" title={details}>BOA</span>;
  if (state === "fair") return <span className="sbc-tag tag-warn" title={details}>REGULAR</span>;
  if (state === "poor") return <span className="sbc-tag tag-offline" title={details}>RUIM</span>;
  if (state === "waiting") return <span className="text-[10px] font-mono text-[color:var(--text-muted)]" title={details}>AGUARDANDO ÁUDIO</span>;
  if (state === "measuring") return <span className="text-[10px] font-mono text-[color:var(--text-muted)]" title={details}>MEDINDO</span>;
  return <span className="text-[10px] font-mono text-[color:var(--text-muted)]">-</span>;
}

export default function PublicExtensionMonitor() {
  const { tenantSlug } = useParams();
  const storageKey = `voxyra-public-monitor:${tenantSlug}`;
  const soundStorageKey = `${storageKey}:sound`;
  const [token, setToken] = useState(() => sessionStorage.getItem(storageKey) || "");
  const [password, setPassword] = useState("");
  const [tenant, setTenant] = useState(null);
  const [data, setData] = useState({ extensions: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [alarmActive, setAlarmActive] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem(soundStorageKey) !== "off");
  const [audioReady, setAudioReady] = useState(false);
  const previousStatuses = useRef(null);
  const audioContext = useRef(null);

  const enableAudio = useCallback(() => {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return null;
    if (!audioContext.current) audioContext.current = new AudioContext();
    audioContext.current.resume().then(() => setAudioReady(true)).catch(() => setAudioReady(false));
    return audioContext.current;
  }, []);

  const playAlarm = useCallback(() => {
    if (!soundEnabled || !audioReady) return;
    const context = audioContext.current;
    if (!context || context.state !== "running") return;
    const now = context.currentTime;
    [0, 0.32].forEach((offset) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(880, now + offset);
      gain.gain.setValueAtTime(0.0001, now + offset);
      gain.gain.exponentialRampToValueAtTime(0.18, now + offset + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.22);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(now + offset);
      oscillator.stop(now + offset + 0.24);
    });
  }, [audioReady, soundEnabled]);

  const logout = useCallback(() => {
    sessionStorage.removeItem(storageKey);
    setToken("");
    setData({ extensions: [], summary: {} });
    setAlarmActive(false);
    previousStatuses.current = null;
  }, [storageKey]);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const response = await api.get(`/public/extension-monitor/${tenantSlug}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const extensions = response.data.extensions || [];
      const nextStatuses = new Map(extensions.map((row) => [row.id || row.numero, !!row.online]));
      const previous = previousStatuses.current;
      const newlyOffline = extensions.filter((row) => {
        if (row.online) return false;
        const key = row.id || row.numero;
        return !previous || !previous.has(key) || previous.get(key) === true;
      });
      if (newlyOffline.length) setAlarmActive(true);
      if (!extensions.some((row) => !row.online)) setAlarmActive(false);
      previousStatuses.current = nextStatuses;
      setData(response.data);
      setTenant(response.data.tenant);
      setError("");
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 404) logout();
      setError(e.response?.data?.detail || "Falha ao atualizar o monitoramento.");
    } finally {
      setLoading(false);
    }
  }, [logout, tenantSlug, token]);

  useEffect(() => {
    if (!alarmActive || !soundEnabled || !audioReady) return undefined;
    playAlarm();
    const timer = setInterval(playAlarm, 6000);
    return () => clearInterval(timer);
  }, [alarmActive, audioReady, playAlarm, soundEnabled]);

  useEffect(() => {
    if (token) {
      load();
      const timer = setInterval(load, 2000);
      return () => clearInterval(timer);
    }
    setLoading(true);
    api.get(`/public/extension-monitor/${tenantSlug}/info`)
      .then((response) => { setTenant(response.data); setError(""); })
      .catch((e) => setError(e.response?.data?.detail || "Monitoramento não encontrado."))
      .finally(() => setLoading(false));
  }, [load, tenantSlug, token]);

  const login = async (event) => {
    event.preventDefault();
    if (soundEnabled) enableAudio();
    setLoading(true);
    setError("");
    try {
      const response = await api.post(`/public/extension-monitor/${tenantSlug}/login`, { password });
      sessionStorage.setItem(storageKey, response.data.token);
      setTenant((current) => current || { name: response.data.name, slug: tenantSlug });
      setToken(response.data.token);
      setPassword("");
    } catch (e) {
      setError(e.response?.data?.detail || "Não foi possível entrar.");
    } finally {
      setLoading(false);
    }
  };

  const summary = data.summary || {};
  const qualityAlerts = useMemo(() => data.extensions.filter((row) => ["fair", "poor"].includes(row.quality?.state)).length, [data.extensions]);
  const offlineExtensions = useMemo(() => data.extensions.filter((row) => !row.online), [data.extensions]);

  const toggleSound = () => {
    if (!soundEnabled || !audioReady) {
      localStorage.setItem(soundStorageKey, "on");
      setSoundEnabled(true);
      enableAudio();
      return;
    }
    localStorage.setItem(soundStorageKey, "off");
    setSoundEnabled(false);
  };

  if (!token) return <main className="min-h-screen bg-[color:var(--bg-primary)] flex items-center justify-center p-4">
    <div className="sbc-card w-full max-w-md p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 border border-[color:var(--border-default)] flex items-center justify-center text-[color:var(--accent-green)]"><ShieldCheck size={20}/></div>
        <div><div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Voxyra IPBX</div><h1 className="text-xl font-semibold">Monitoramento de ramais</h1></div>
      </div>
      <div className="text-sm text-[color:var(--text-secondary)] mb-5">{tenant?.name || "Acesso do cliente"}</div>
      <form onSubmit={login} className="space-y-4">
        <div><label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] block mb-1">Senha de acesso</label><input className="sbc-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus autoComplete="current-password"/></div>
        {error && <div className="text-xs font-mono text-[color:var(--accent-red)]">{error}</div>}
        <button className="sbc-btn sbc-btn-primary w-full justify-center" type="submit" disabled={loading}>{loading ? <RefreshCw size={14} className="animate-spin"/> : <ShieldCheck size={14}/>} Login</button>
      </form>
    </div>
  </main>;

  return <main className="min-h-screen bg-[color:var(--bg-primary)] p-4 md:p-6 space-y-5">
    <header className="flex items-start justify-between gap-4">
      <div><div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Voxyra IPBX · Tempo real</div><h1 className="text-2xl font-semibold tracking-tight">Monitoramento de ramais</h1><div className="text-sm text-[color:var(--text-secondary)] mt-1">{tenant?.name}</div></div>
      <div className="flex gap-2"><button className="sbc-btn" onClick={load} title="Atualizar"><RefreshCw size={14} className={loading ? "animate-spin" : ""}/></button><button className="sbc-btn" onClick={logout} title="Deslogar"><LogOut size={14}/></button></div>
    </header>
    {error && <div className="text-xs font-mono text-[color:var(--accent-red)]">{error}</div>}
    {offlineExtensions.length > 0 && <section aria-live="assertive" className={`border border-[rgba(239,68,68,0.65)] bg-[rgba(239,68,68,0.10)] p-4 ${alarmActive ? "animate-pulse" : ""}`}>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 shrink-0 border border-[rgba(239,68,68,0.55)] flex items-center justify-center text-[color:var(--accent-red)]"><Bell size={20}/></div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--accent-red)]">{alarmActive ? "Alarme ativo" : "Alerta reconhecido"}</div>
            <h2 className="text-base font-semibold">{offlineExtensions.length} ramal(is) offline</h2>
            <div className="flex flex-wrap gap-2 mt-2">{offlineExtensions.map((row) => <span key={row.id || row.numero} className="sbc-tag tag-offline">{row.numero} · {row.nome}</span>)}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 lg:justify-end">
          {alarmActive && <button className="sbc-btn" onClick={() => setAlarmActive(false)} title="Parar o alarme até uma nova queda"><CheckCircle2 size={14}/> Reconhecer alerta</button>}
          <button className="sbc-btn" onClick={toggleSound} title={soundEnabled && audioReady ? "Silenciar alarme" : "Ativar alarme sonoro"}>{soundEnabled && audioReady ? <Volume2 size={14}/> : <VolumeX size={14}/>} {soundEnabled && audioReady ? "Som ativado" : "Ativar som"}</button>
        </div>
      </div>
    </section>}
    <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <div className="sbc-card p-4"><div className="text-[10px] uppercase text-[color:var(--text-muted)]">Online</div><div className="text-2xl font-mono mt-1 text-[color:var(--accent-green)]">{summary.extensions_online || 0}</div></div>
      <div className="sbc-card p-4"><div className="text-[10px] uppercase text-[color:var(--text-muted)]">Total</div><div className="text-2xl font-mono mt-1">{summary.extensions_total || 0}</div></div>
      <div className="sbc-card p-4"><div className="text-[10px] uppercase text-[color:var(--text-muted)]">Em chamada</div><div className="text-2xl font-mono mt-1">{summary.extensions_in_call || 0}</div></div>
      <div className="sbc-card p-4"><div className="text-[10px] uppercase text-[color:var(--text-muted)]">Alertas de qualidade</div><div className={`text-2xl font-mono mt-1 ${qualityAlerts ? "text-[color:var(--accent-red)]" : "text-[color:var(--accent-green)]"}`}>{qualityAlerts}</div></div>
    </section>
    <section className="sbc-card overflow-auto">
      <table className="sbc-table min-w-[1180px]"><thead><tr><th>Ramal</th><th>IP externo</th><th>IP interno</th><th>Porta</th><th>Transporte</th><th>Aparelho</th><th>Expira</th><th>Chamada</th><th>Qualidade</th><th>Registro</th></tr></thead>
        <tbody>{data.extensions.map((row) => <tr key={row.id} className={!row.online ? "bg-[rgba(239,68,68,0.06)]" : ""}><td><div className="font-mono font-medium">{row.numero}</div><div className="text-[10px] text-[color:var(--text-muted)]">{row.nome}</div></td><td className="font-mono text-xs">{row.network_ip || "-"}</td><td className="font-mono text-xs">{row.contact_ip || "-"}</td><td className="font-mono text-xs">{row.network_port || row.contact_port || "-"}</td><td className="font-mono text-xs">{row.transport || "-"}</td><td className="text-xs max-w-[220px] truncate" title={row.user_agent || ""}>{row.user_agent || "-"}</td><td className="font-mono text-xs">{row.online && row.expires != null ? `${row.expires}s` : "-"}</td><td><CallState state={row.call_state}/></td><td><CallQuality quality={row.quality}/></td><td>{row.online ? <span className="sbc-tag tag-online">REGISTRADO{row.contacts > 1 ? ` · ${row.contacts}` : ""}</span> : <span className="sbc-tag tag-offline">OFFLINE</span>}</td></tr>)}</tbody>
      </table>
      {!loading && !data.extensions.length && <div className="p-8 text-center text-sm text-[color:var(--text-muted)]"><Activity size={20} className="mx-auto mb-2"/>Nenhum ramal cadastrado.</div>}
    </section>
    <div className="text-[10px] font-mono text-[color:var(--text-muted)]">Atualizado em {data.updated_at ? new Date(data.updated_at).toLocaleString("pt-BR") : "-"}</div>
  </main>;
}
